import { ReactNode, useContext } from 'react';
import {
  IWebSocketData,
  useWSSubscriber,
} from '@axo/shared/feature/web-socket';
import {
  ace,
  assignment,
  event,
  loan_application,
  loan_quote_accept,
  lock,
  sms,
  payment_remark,
} from '@axo/shared/data-access/types';
import {
  SMSCommunicationKeys,
  applicationKeys,
  notificationKeys,
  userKeys,
  paymentRemarkKeys,
  callsKeys,
  bucketsKeys,
} from '@axo/shared/data-access/hooks';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { useQueryClient } from 'react-query';
import { ApplicationContext } from '../../store/context';

export function WSSubscriber({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const { state } = useContext(DataAccessContext);
  const { state: applicationState } = useContext(ApplicationContext);

  const assignmentSubscriptions = [
    {
      source: 'assignment',
      code: 'assignment-updated',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) return;
        const event =
          message.latestMessage as event.Event<assignment.Assignment>;
        const assignment = event?.data.new ?? event?.data.old;
        if (assignment && assignment?.User === state.user.clientID) {
          client.invalidateQueries(
            userKeys.assignedApplications({
              user: assignment.User,
              country: assignment.Country,
            }),
          );
        }
      },
    },
  ];

  const assigneeSubscriptions = [
    {
      source: 'assignee',
      code: 'assignee-available',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) return;
        const event = message.latestMessage as event.Event<assignment.Assignee>;
        const userID = event.data?.id;
        if (!userID || event.marketCountry) return;
        client.invalidateQueries(
          userKeys.assignee({
            user: userID,
            country: event.marketCountry,
          }),
        );
      },
    },
    {
      source: 'assignee',
      code: 'assignee-unavailable',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) return;
        const event = message.latestMessage as event.Event<assignment.Assignee>;
        const userID = event.data?.id;
        if (!userID || event.marketCountry) return;
        client.invalidateQueries(
          userKeys.assignee({
            user: userID,
            country: event.marketCountry,
          }),
        );
      },
    },
  ];

  const lockSubcriptions = [
    {
      source: 'lock',
      code: 'loan-application-unlocked',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) return;
        const event = message.latestMessage as event.Event<lock.Lock>;
        const wasLockedByCurrentUser =
          event.data?.old?.LockedBy === state.user.clientID;
        const hasApplicationOpen = Object.keys(
          applicationState.openApplications,
        ).includes(event.data?.old?.LoanApplicationID ?? '');
        if (wasLockedByCurrentUser && event.data?.old?.LockedBy) {
          client.invalidateQueries(
            userKeys.lockedApplications({
              user: event.data.old.LockedBy,
              country: event.marketCountry,
            }),
          );
        }
        // Update UI instantly - not waiting for window to be focused.
        if (hasApplicationOpen) {
          client.invalidateQueries(
            applicationKeys.locks({
              applicationID: event.data.id,
              marketCountry: event.marketCountry,
            }),
          );
        }
      },
    },
    {
      source: 'lock',
      code: 'loan-application-locked',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) return;
        const event = message.latestMessage as event.Event<lock.Lock>;
        console.log(event);
        const isBeingLockedByCurrentUser =
          event.data?.new?.LockedBy === state.user.clientID;
        const hasApplicationOpen = Object.keys(
          applicationState.openApplications,
        ).includes(event.data?.new?.LoanApplicationID ?? '');
        if (isBeingLockedByCurrentUser && event.data?.new?.LockedBy) {
          client.invalidateQueries(
            userKeys.lockedApplications({
              user: event.data.new.LockedBy,
              country: event.marketCountry,
            }),
          );
        }
        // Update UI instantly - not waiting for window to be focused.
        if (hasApplicationOpen) {
          client.invalidateQueries(
            applicationKeys.locks({
              applicationID: event.data.id,
              marketCountry: event.marketCountry,
            }),
          );
        }
      },
    },
  ];

  const loanQuoteAcceptSubcriptions = [
    {
      source: 'loan-quote-accept',
      code: 'loan-quote-accept-updated',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) return;
        const event =
          message.latestMessage as event.Event<loan_quote_accept.LoanQuoteAccept>;
        const appId = event?.data.new?.LoanApplicationID;
        if (!appId) return;
        const hasApplicationOpen = Object.keys(
          applicationState.openApplications,
        ).includes(appId);
        if (hasApplicationOpen) {
          client.invalidateQueries(
            applicationKeys.loanQuotes({
              applicationID: appId,
              marketCountry: event.marketCountry,
            }),
          );
        }
      },
    },
  ];

  const SMSSubscriptions = [
    {
      source: 'sms-communication',
      code: 'sms-communication-created',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) return;
        const event = message.latestMessage as event.Event<sms.SMS>;
        if (event.data?.new?.CustomerPhoneNumber) {
          client.invalidateQueries(
            SMSCommunicationKeys.root({
              customerPhoneNumber: event.data.new.CustomerPhoneNumber,
            }),
          );
        }
      },
    },
    {
      source: 'sms-communication',
      code: 'sms-communication-updated',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) return;
        const event = message.latestMessage as event.Event<sms.SMS>;
        if (event.data?.new?.CustomerPhoneNumber) {
          client.invalidateQueries(
            SMSCommunicationKeys.root({
              customerPhoneNumber: event.data.new.CustomerPhoneNumber,
            }),
          );
        }
      },
    },
  ];

  const aceSubs = [
    {
      source: 'ace',
      code: 'ace-notification-created',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) {
          return;
        }

        const event = message.latestMessage as event.Event<ace.Notification>;

        if (
          event.data?.new?.ClientID &&
          event.data.new.ClientID === state.user.clientID
        ) {
          client.invalidateQueries(
            notificationKeys.unread({
              user: event.data.new.ClientID,
              country: event.marketCountry,
            }),
          );
        }
      },
    },
    {
      source: 'ace',
      code: 'ace-call-record-created',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) {
          return;
        }

        const event = message.latestMessage as event.Event<ace.CallRecord>;

        if (event.data.new) {
          client.invalidateQueries([
            callsKeys.records({
              LoanApplicationID: event.data.new.LoanApplicationID,
            }),
          ]);
        }
      },
    },
    {
      source: 'ace',
      code: 'ace-call-record-updated',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) {
          return;
        }

        const event = message.latestMessage as event.Event<ace.CallRecord>;

        if (event.data.new) {
          client.invalidateQueries([
            callsKeys.records({
              LoanApplicationID: event.data.new.LoanApplicationID,
            }),
          ]);
        }
      },
    },
    {
      source: 'ace',
      code: 'ace-application-bucket-assigned',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) {
          return;
        }

        const event =
          message.latestMessage as event.Event<ace.ApplicationBucket>;

        if (event.data.new) {
          client.invalidateQueries([bucketsKeys.root()]);
        }
      },
    },
    {
      source: 'ace',
      code: 'ace-application-bucket-moved',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) {
          return;
        }

        const event =
          message.latestMessage as event.Event<ace.ApplicationBucket>;

        if (event.data.new) {
          client.invalidateQueries([bucketsKeys.root()]);
        }
      },
    },
  ];

  const applicationExtraInformationSubscriptions = [
    {
      source: 'loan-application',
      code: 'application-extra-information-created',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) return;
        const event =
          message.latestMessage as event.Event<loan_application.ApplicationExtraInformation>;
        const appId = event.data?.new?.LoanApplicationID;

        if (!appId) return;

        client.invalidateQueries(applicationKeys.extraInfo(appId));
      },
    },
    {
      source: 'loan-application',
      code: 'application-extra-information-updated',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) return;
        const event =
          message.latestMessage as event.Event<loan_application.ApplicationExtraInformation>;
        const appId =
          event.data?.new?.LoanApplicationID ??
          event.data?.old?.LoanApplicationID;

        if (!appId) return;

        client.invalidateQueries(applicationKeys.extraInfo(appId));
      },
    },
  ];

  const paymentRemarkSubscriptions = [
    {
      source: 'payment-remark',
      code: 'payment-remark-trigger-completed',
      subscriberCallback: <T,>(message: IWebSocketData<T>) => {
        if (!message.latestMessage) return;
        const event =
          message.latestMessage as event.Event<payment_remark.Trigger>;
        if (event.data?.new?.PersonID) {
          client.invalidateQueries(
            paymentRemarkKeys.root({
              personID: event.data.new.PersonID,
            }),
          );
        }
      },
    },
  ];

  useWSSubscriber([
    ...assignmentSubscriptions,
    ...assigneeSubscriptions,
    ...lockSubcriptions,
    ...loanQuoteAcceptSubcriptions,
    ...SMSSubscriptions,
    ...aceSubs,
    ...applicationExtraInformationSubscriptions,
    ...paymentRemarkSubscriptions,
  ]);

  return <>{children}</>;
}
